import { z } from 'zod';

/**
 * Generate a URL-friendly slug from a string.
 */
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Step 1: Basic Info validation schema.
 */
export const step1Schema = z.object({
  name: z
    .string()
    .min(1, 'Workspace name is required')
    .max(255, 'Name must be 255 characters or fewer'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(255, 'Slug must be 255 characters or fewer')
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      'Slug must contain only lowercase letters, numbers, and hyphens'
    ),
  industry: z.string().optional(),
  website: z
    .string()
    .url('Please enter a valid URL')
    .max(500, 'URL must be 500 characters or fewer')
    .optional()
    .or(z.literal('')),
  description: z.string().max(500, 'Description must be 500 characters or fewer').optional(),
});

/**
 * Step 2: Branding & Settings validation schema.
 */
export const step2Schema = z.object({
  logoFile: z.instanceof(File).optional().nullable(),
  logoPreview: z.string().optional().nullable(),
  timezone: z.string().min(1, 'Timezone is required'),
  defaultLanguage: z.string().min(1, 'Language is required'),
});

/**
 * Full create workspace form data.
 */
export const createWorkspaceFormSchema = step1Schema.merge(step2Schema);

export type Step1Data = z.infer<typeof step1Schema>;
export type Step2Data = z.infer<typeof step2Schema>;
export type CreateWorkspaceFormData = z.infer<typeof createWorkspaceFormSchema>;